import { isEmpty } from 'lodash';
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { gradeApi } from '../../../api.js';
import { useAuth } from '../../../auth/AuthContext';
import DragAndDropUpload from '../../DragDropExamUpload';
import './styling/EnhancedUpload.scss';
import { GRADING_STATUS } from '../constants.js';
import './styling/ExamUpload.scss';
import { ArrowRight, CheckCircle, X, AlertTriangle, FileUp, Files, BookOpen } from 'lucide-react';
import { toast } from 'react-hot-toast';
import GoogleClassroomImportWizard from './GoogleClassroomImportWizard';

function ExamUpload({ assignment_id, setUpdateTrigger }) {
  // Basic states
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [details, setDetails] = useState([]);
  const [pollingDetails, setPollingDetails] = useState([]);
  const { user } = useAuth();
  const pollIntervalRef = useRef(null);
  const [numPages, setNumPages] = useState(0);

  // Upload mode selection
  const [uploadMode, setUploadMode] = useState(null); // 'combined' or 'individual'
  const [uploadModeChosen, setUploadModeChosen] = useState(false);

  // Pages per submission state (only used in combined mode)
  const [pagesPerSubmission, setPagesPerSubmission] = useState('');
  const [oddPagesWarning, setOddPagesWarning] = useState('');

  // Alignment markers setting
  const [doNotUseAlignmentMarkers, setDoNotUseAlignmentMarkers] = useState(false);
  const useTextAlignment = !doNotUseAlignmentMarkers;
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);

  const checkNumberOfPages = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/problem-set/num-pages/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking number of pages:', error);
      setErrorMessage('Failed to upload file. Please try again or contact support.');
      return null;
    }
  };

  // Handle file selection for batch files
  const handleFilesChange = selectedFiles => {
    console.log('Files change handler called with:', selectedFiles);

    // Check if this is a file removal (array with fewer items than before)
    if (Array.isArray(selectedFiles)) {
      console.log('Setting files directly:', selectedFiles);
      setFiles(selectedFiles);
      return;
    }

    if (!selectedFiles || (selectedFiles instanceof FileList && selectedFiles.length === 0)) {
      console.log('No files selected');
      return;
    }

    // Convert FileList to array if needed
    const newFilesArray = Array.isArray(selectedFiles) ? selectedFiles : Array.from(selectedFiles);

    // If we already have files, check for duplicates
    if (files.length > 0) {
      const updatedFiles = [...files];
      const duplicates = [];

      // Check each new file for duplicates
      newFilesArray.forEach(newFile => {
        const isDuplicate = files.some(
          existingFile => existingFile.name === newFile.name && existingFile.size === newFile.size
        );

        if (isDuplicate) {
          duplicates.push(newFile.name);
        } else {
          updatedFiles.push(newFile);
        }
      });

      // Show toast for duplicates
      if (duplicates.length > 0) {
        const duplicateNames =
          duplicates.length > 2
            ? `${duplicates.slice(0, 2).join(', ')} and ${duplicates.length - 2} more`
            : duplicates.join(', ');

        toast.error(`Duplicate file${duplicates.length > 1 ? 's' : ''} skipped: ${duplicateNames}`);
      }

      setFiles(updatedFiles);
    } else {
      // If no existing files, just set the new files
      setFiles(newFilesArray);
    }
  };

  const checkGradingStatus = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/batch-upload/status`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      console.log('Grading status response:', response.data);

      // Extract and set the polling details which include invalid files warnings
      if (response.data && response.data.details) {
        // Store the details in state so we can access them in the render
        setPollingDetails(response.data.details);

        return {
          status: response.data.status || 'unknown',
          pollingDetails: response.data.details
        };
      }
      return { status: response.data?.status || 'unknown', pollingDetails: [] };
    } catch (error) {
      console.error('Error checking grading status:', error);
      return { status: 'error', pollingDetails: [] };
    }
  };

  const cancelGradingStatus = async () => {
    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/batch-upload/status`,
        body: {},
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
    } catch (error) {
      console.error('Error canceling grading status:', error);
      return false;
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      const { status, details } = await checkGradingStatus();
      if (status === GRADING_STATUS.processed) {
        setSuccessMessage('Assignments graded successfully! Scroll down to see grades');
        setErrorMessage('');
        setDetails(details);
      }

      if (status === GRADING_STATUS.failed) {
        setErrorMessage('Assignments grading failed!');
        setDetails(details);
      }

      if (status === GRADING_STATUS.in_progress) {
        startPolling();
      }
    };

    checkStatus();
  }, []);

  const startPolling = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setIsPolling(true);
    pollIntervalRef.current = setInterval(async () => {
      const { status, pollingDetails } = await checkGradingStatus();
      console.log('Polling status:', status);

      // Only update the process status, don't overwrite our warning details
      if (status !== GRADING_STATUS.in_progress) {
        clearInterval(pollIntervalRef.current);
        setIsPolling(false);
        setLoading(false);
        if (status === GRADING_STATUS.failed) {
          setErrorMessage('Assignments grading failed');
          setSuccessMessage('');
        } else {
          setSuccessMessage('Assignments graded successfully! Scroll down to see grades');
          setErrorMessage('');
        }

        // Update details but preserve any invalidFiles information
        setDetails(prevDetails => {
          // If previous details had information about invalid files, preserve it
          if (typeof prevDetails === 'object' && prevDetails.invalidFiles > 0) {
            return {
              ...prevDetails,
              processingDetails: pollingDetails // Store polling details separately
            };
          }
          // Otherwise just use the new details
          return pollingDetails;
        });

        setUpdateTrigger(prev => !prev);
      } else {
        // During in-progress polling, don't reset the invalidFiles information
        // Just update other status information if needed
      }
    }, 5000);
  };

  const handleCancel = async () => {
    await cancelGradingStatus();
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    setIsPolling(false);
    setLoading(false);
    setErrorMessage('Manually canceled by teacher!');
  };

  const uploadAndGradeAssignments = async (
    e,
    discardBlankPages = false,
    bypassModalCheck = false
  ) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setOddPagesWarning('');

    // Validate files are selected
    if (!files || files.length === 0) {
      setErrorMessage('Please select at least one file to upload.');
      setLoading(false);
      return;
    }

    // For combined mode, validate pages per submission
    if (uploadMode === 'combined') {
      if (!pagesPerSubmission || isNaN(pagesPerSubmission) || parseInt(pagesPerSubmission) <= 0) {
        setErrorMessage('Please enter a valid number of pages per submission.');
        setLoading(false);
        return;
      }
    }

    const formData = new FormData();

    // Append all files
    files.forEach((file, index) => {
      formData.append(`file_data_${index}`, file);
    });

    // Set upload mode in the request
    formData.append('upload_mode', uploadMode);
    formData.append('file_count', files.length);

    // Only add pages_per_submission for combined mode
    if (uploadMode === 'combined') {
      formData.append('pages_per_submission', pagesPerSubmission);
    }

    formData.append('user_id', user.user.id);
    formData.append('discard_blank_pages', discardBlankPages);
    formData.append('use_text_alignment', useTextAlignment);

    try {
      const uploadResponse = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/batch-upload/`,
        body: formData,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      });

      if (uploadResponse.status === 200) {
        setSubmitted(true);

        // Get the response data directly from uploadResponse.data
        const responseData = uploadResponse.data;
        console.log('Upload response:', responseData); // Debug log

        if (responseData && responseData.invalid_files > 0) {
          // Show a warning toast about excluded files with clear styling
          toast.error(
            `Warning: ${responseData.invalid_files} file(s) excluded due to invalid page counts!`,
            {
              duration: 8000,
              style: {
                border: '1px solid #f43f5e',
                padding: '16px',
                color: '#9f1239'
              },
              icon: '⚠️'
            }
          );

          // Set details for display
          setDetails({
            validFiles: responseData.valid_files,
            invalidFiles: responseData.invalid_files,
            details: responseData.details || []
          });

          // Show a separate success toast for the valid files
          if (responseData.valid_files > 0) {
            toast.success(`Processing ${responseData.valid_files} valid file(s).`);
          }
        } else {
          // All files were valid
          toast.success('Files uploaded successfully. Processing started.');
        }

        startPolling();
      } else {
        setErrorMessage('Failed to upload. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error uploading or grading assignments:', error);
      setErrorMessage(error.response?.data?.error || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Function to handle pages per submission input change
  const handlePagesPerSubmissionChange = e => {
    const value = e.target.value;
    setPagesPerSubmission(value);

    // Show warning if pages per submission is an odd number
    if (value && !isNaN(value) && parseInt(value) > 0) {
      const pages = parseInt(value);
      if (pages % 2 !== 0) {
        setOddPagesWarning(
          `You've entered ${pages} pages, which is an odd number. When scanning double-sided documents, blank trailing pages are often included. Please verify if ${pages + 1} would be more accurate for your scanned submissions.`
        );
      } else {
        setOddPagesWarning('');
      }
    } else {
      setOddPagesWarning('');
    }
  };

  return (
    <div className="exam-upload-container">
      <div className="exam-upload-header">
        <h2>Step 3: Upload Student Work</h2>
        <p>Upload batch PDFs of student submissions</p>
      </div>

      {/* Upload form section */}
      <div className="upload-section">
        {/* Upload Mode Selection */}
        <div className="upload-mode-selection">
          <h3>Select Upload Mode</h3>
          <div className="upload-mode-options">
            <div
              className={`upload-mode-option ${uploadMode === 'combined' ? 'selected' : ''}`}
              onClick={() => {
                if (!loading && !isPolling) {
                  setUploadMode('combined');
                  setUploadModeChosen(true);
                }
              }}
            >
              <FileUp size={18} />
              <div className="option-text">
                <h4>Scanned Worksheets</h4>
                <p>
                  Each PDF contains complete, batched student submissions. All submissions must have
                  the same number of pages.
                </p>
              </div>
            </div>

            <div
              className={`upload-mode-option ${uploadMode === 'individual' ? 'selected' : ''}`}
              onClick={() => {
                if (!loading && !isPolling) {
                  setUploadMode('individual');
                  setUploadModeChosen(true);
                }
              }}
            >
              <Files size={18} />
              <div className="option-text">
                <h4>Downloaded Submissions</h4>
                <p>
                  Upload a Zip or folder of individual student DOCX or PDF files (e.g., from Canvas
                  or Google Classroom).
                </p>
              </div>
            </div>

            <div
              className={`upload-mode-option ${uploadMode === 'google_classroom' ? 'selected' : ''}`}
              onClick={() => {
                if (!loading && !isPolling) {
                  setUploadMode('google_classroom');
                  setUploadModeChosen(true);
                }
              }}
            >
              <BookOpen size={18} />
              <div className="option-text">
                <h4>Import from Google Classroom</h4>
                <p>
                  Import student submissions directly from your Google Classroom.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Conditionally rendered upload components */}
        {uploadModeChosen && (
          <>
            {uploadMode === 'google_classroom' ? (
              <GoogleClassroomImportWizard
                assignmentId={assignment_id}
                onImportComplete={() => {
                  setSuccessMessage('Submissions imported successfully from Google Classroom!');
                  setUpdateTrigger(prev => !prev);
                  setUploadMode(null);
                  setUploadModeChosen(false);
                }}
                onCancel={() => {
                  setUploadMode(null);
                  setUploadModeChosen(false);
                }}
                isUploading={loading}
                setIsUploading={setLoading}
              />
            ) : (
              <div className="batch-files-upload">
                <DragAndDropUpload
                  files={files}
                  multiple={true}
                  disabled={loading || isPolling}
                  acceptDocx={uploadMode === 'individual'}
                  acceptZip={true}
                  onFileChange={handleFilesChange}
                />

                {uploadMode === 'combined' && (
                  <div className="enhanced-pages-input">
                    <label htmlFor="pagesPerSubmission">
                      Pages per submission: <span className="required">*</span>
                    </label>
                    <input
                      id="pagesPerSubmission"
                      type="number"
                      min="1"
                      max="30"
                      value={pagesPerSubmission}
                      placeholder="Number"
                      disabled={loading || isPolling}
                      className={loading || isPolling ? 'disabled' : ''}
                      onChange={handlePagesPerSubmissionChange}
                      onWheel={e => e.target.blur()}
                    />
                    <p className="input-help-text">
                      Each student submission must have exactly this many pages
                    </p>
                    {oddPagesWarning && (
                      <p className="text-amber-600 mt-2 text-sm flex items-start">
                        <AlertTriangle
                          size={14}
                          className="inline mr-1 mt-1 flex-shrink-0 text-amber-600"
                        />
                        <span>{oddPagesWarning}</span>
                      </p>
                    )}
                  </div>
                )}

                {files && files.length > 0 && (
                  <div className="batch-info">
                    <h4>Upload Summary</h4>
                    <ul>
                      <li>
                        <strong>{files.length}</strong> file{files.length !== 1 ? 's' : ''} selected
                      </li>
                      {uploadMode === 'combined' &&
                        (pagesPerSubmission ? (
                          <li>
                            <strong>{pagesPerSubmission}</strong> pages per student submission
                          </li>
                        ) : (
                          <li className="warning">
                            <AlertTriangle size={12} />
                            <span>Pages per submission is missing</span>
                          </li>
                        ))}
                    </ul>
                    {uploadMode === 'combined' ? (
                      <p className="info-text">
                        The system will merge all files, check if they're divisible by the pages per
                        submission, and process them automatically. Any files with incorrect page
                        counts will be reported.
                      </p>
                    ) : (
                      <p className="info-text">
                        Each file will be processed as a separate student submission. DOCX files will
                        be automatically converted to PDF format before processing.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Button section */}
      {/* Validation message */}
      {!loading && !isPolling && uploadMode !== 'google_classroom' && (
        <div className="validation-message">
          {!files || files.length === 0 ? (
            <p className="text-amber-600">
              <AlertTriangle size={14} className="inline mr-1" /> Please select at least one file
            </p>
          ) : uploadMode === 'combined' && !pagesPerSubmission ? (
            <p className="text-amber-600">
              <AlertTriangle size={14} className="inline mr-1" /> Please specify pages per
              submission
            </p>
          ) : (
            <p className="text-green-600">
              <CheckCircle size={14} className="inline mr-1" /> Ready to upload
            </p>
          )}
        </div>
      )}

      {uploadMode !== 'google_classroom' && (
        <div className="action-section">
          <button
            disabled={
              !files ||
              files.length === 0 ||
              (uploadMode === 'combined' && !pagesPerSubmission) ||
              loading ||
              isPolling
            }
            className={`proceed-btn ${!files || files.length === 0 || (uploadMode === 'combined' && !pagesPerSubmission) ? 'btn-disabled' : loading || isPolling ? 'disabled' : ''}`}
            onClick={uploadAndGradeAssignments}
          >
            <span className={loading || isPolling ? 'btn-text-hidden' : 'btn-text'}>
              {submitted ? 'Reupload & Grade' : 'Upload & Grade'}
            </span>
            {!(loading || isPolling) && <ArrowRight size={16} />}
          </button>

          {(loading || isPolling) && (
            <button className="cancel-btn" onClick={handleCancel}>
              <X size={12} />
              <span>Cancel</span>
            </button>
          )}
        </div>
      )}

      {/* Status messages */}
      {errorMessage && (
        <div className="status-message error">
          <X size={12} />
          <p>{errorMessage}</p>
        </div>
      )}

      {successMessage && (
        <div className="status-message success">
          <CheckCircle size={24} />
          <p>{successMessage}</p>
        </div>
      )}

      {(loading || isPolling) && (
        <>
          <div className="status-message processing">
            <div className="processing-spinner"></div>
            <p>Grading in progress, you can safely leave this page...</p>
          </div>

          {/* Show invalid files warning as part of the processing UI */}
          {pollingDetails &&
            Array.isArray(pollingDetails) &&
            pollingDetails.some(
              detail => typeof detail === 'object' && detail.type === 'invalid_files_warning'
            ) && (
              <div className="status-message warning processing-warning">
                <AlertTriangle size={14} />
                <div>
                  {pollingDetails
                    .filter(
                      detail =>
                        typeof detail === 'object' && detail.type === 'invalid_files_warning'
                    )
                    .map(warning => {
                      const warningData = warning.data || {};
                      const invalidCount = warningData.invalid_files_count || 0;
                      const invalidDetails = warningData.invalid_files_details || [];

                      return (
                        <div key={nanoid()}>
                          <p className="warning-title">
                            Warning: {invalidCount} file(s) excluded due to invalid page counts
                          </p>
                          {invalidDetails.length > 0 && (
                            <>
                              <p className="warning-subtitle">
                                The following files were not processed:
                              </p>
                              <ul className="warning-list">
                                {invalidDetails.map(detail => (
                                  <li key={nanoid()}>{detail}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
        </>
      )}

      {/* Invalid files warning banner - shown immediately */}
      {typeof details === 'object' && details.invalidFiles > 0 && (
        <div className="status-message warning">
          <AlertTriangle size={14} />
          <div>
            <p className="warning-title">
              Warning: {details.invalidFiles} file(s) excluded due to invalid page counts
            </p>
            <p className="warning-subtitle">The following files were not processed:</p>
            <ul className="warning-list">
              {details.details && details.details.map(info => <li key={nanoid()}>{info}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Processing details */}
      {!isEmpty(details) && !(loading || isPolling) && (
        <div className="processing-details">
          <h3>Upload Results</h3>
          <div className="details-content">
            {typeof details === 'object' && details.invalidFiles > 0 ? (
              <>
                <div className="summary-info">
                  <p>
                    <strong>{details.validFiles}</strong> valid file(s) were processed.
                  </p>
                  <p>
                    <strong>{details.invalidFiles}</strong> file(s) were excluded due to invalid
                    page counts.
                  </p>
                </div>
                <h4>Invalid Files</h4>
                {details.details &&
                  details.details.map(info => (
                    <p key={nanoid()} className="detail-item error-item">
                      {info}
                    </p>
                  ))}
              </>
            ) : Array.isArray(details) ? (
              details.map(info => (
                <p key={nanoid()} className="detail-item">
                  {info}
                </p>
              ))
            ) : (
              <p>{JSON.stringify(details)}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamUpload;
